import { captureException } from '@sentry/nextjs'
import { useEffect } from 'react'

import useAppQueryState from '@hooks/useAppQueryState'

import { useAlternativeProductCategoriesQuery } from '@queries/useAlternativeProductCategoriesQuery'
import { useEligibilityFactorsQuery } from '@queries/useEligibilityFactorsQuery'
import { useScoredCategoriesQuery } from '@queries/useScoredCategoriesQuery'

import getRedirectToAlternativeProducts from '@routes/ScoredTableRoute/utils/getRedirectToAlternativeProducts'

import AnimatedLoadingPage from '@components/AnimatedLoadingPage'
import ErrorBoundary from '@components/ErrorBoundary'

import { categories } from '@config/categories'

import AlternativeProductsTable from './components/AlternativeProductsTable'
import AlternativeProductsTableError from './components/AlternativeProductsTableError'

const AlternativeProductsTableRoute = () => {
    const { appQueryState, goToRouteWithAppState } = useAppQueryState()

    const scoredCategoriesResponse = useScoredCategoriesQuery({
        body: {
            scoreId: appQueryState.scoreId!,
            customerId: appQueryState.customerId!,
            category: appQueryState.categoryId!,
        },
        select: data => data,
    })

    const shouldFetchAlternativeProducts = getRedirectToAlternativeProducts(
        scoredCategoriesResponse
    )

    const alternativeProductsResponse = useAlternativeProductCategoriesQuery({
        body: {
            scoreId: appQueryState.scoreId!,
            customerId: appQueryState.customerId!,
        },
        shouldFetchAlternativeProducts,
    })

    const eligibilityFactorsResponse = useEligibilityFactorsQuery({
        body: {
            customerId: appQueryState.customerId!,
            scoreId: appQueryState.scoreId!,
        },
        shouldFetchEligibilityFactors: shouldFetchAlternativeProducts,
    })

    useEffect(() => {
        if (
            scoredCategoriesResponse.isSuccess &&
            !shouldFetchAlternativeProducts
        ) {
            goToRouteWithAppState(categories.BestCards.path)
        }
    }, [scoredCategoriesResponse.isSuccess, shouldFetchAlternativeProducts])

    switch (true) {
        case scoredCategoriesResponse.isError:
            return (
                <AlternativeProductsTableError
                    error={scoredCategoriesResponse.error}
                />
            )
        case scoredCategoriesResponse.isLoading:
        case alternativeProductsResponse.isInitialLoading:
        case eligibilityFactorsResponse.isInitialLoading:
        case !shouldFetchAlternativeProducts:
            return <AnimatedLoadingPage />
        case alternativeProductsResponse.isSuccess &&
            alternativeProductsResponse.data != null:
            return (
                <ErrorBoundary
                    fallback={<AlternativeProductsTableError />}
                    onError={({ error }) => captureException(error)}
                >
                    <AlternativeProductsTable
                        alternativeProductCategories={
                            alternativeProductsResponse.data?.categories!
                        }
                        numberOfRedEligibilityFactors={
                            eligibilityFactorsResponse?.data?.red
                        }
                    />
                </ErrorBoundary>
            )
        case alternativeProductsResponse.isError:
        default:
            return <AlternativeProductsTableError />
    }
}

export default AlternativeProductsTableRoute
